import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { DefaultGrid } from '../../../../components/grids/default/defaultGrid'
import { PluginWrapper, ToolbarWrapper } from '../../../../components/plugins'
import { CommandsPanel } from './CommandsPanel'
import useGridFilter, { CreateSelectorGridFilter } from '../../../../system/hooks/useGridFilter'
import styles from '../../../styles/index.module.scss'
import styles2 from './BunchInvoices.module.scss'
import { TextInput } from '../../../../components/controls/inputs'
import BaseBunchInvoicesCreator from './BunchInvoicesForm/BaseBunchInvocesCreator'
import { BunchType, IBunchItemBody, IImportedInvoiceItemDTO, IImportedInvoiceItemViewDTO } from '../../../../libs/coreapi-dto/documents/importedInvoiceItem'
import { useAppContext } from '../../../../system/providers/appContextProvider'
import { GoodsDataProvider } from '../../../../Services/DataProviders/GoodsDataProvider'
import { RegisterPriceDataProvider } from '../../../../Services/DataProviders/RegisterPriceDataProvider'
import { IGoodsDTO, IGoodsViewDTO } from '../../../../libs/coreapi-dto/dirs/goods'
import { v4 as uuidv4 } from 'uuid';
import { useTabsContext } from '../../../../system/providers/tabsProvider'
import BunchGrid from './BunchGrid/BunchGrid'
import { isArray } from 'lodash'
import { MessageModalWindow } from '../../../../components/modalWindows/MessageModalWindow'
import { ImportedInvoiceDataProvider } from '../../../../Services/DataProviders/ImportedInvoiceDataProvider'
import { IDataForMoveFiles, IPostFilesNameDTO } from '../../../../libs/coreapi-dto/dirs/importInvoices'
import { ImportInvoicesProvider } from '../../../../Services/DataProviders/ImportInvoicesProvider';
import { SearchButton } from '../../../../components/buttons/iconButtons/action/MainCollection'
import GoodsBunchGridPluginSettings from './GoodsBunchGridPluginSettings'
import { Spinner } from '../../../../components/spiner/Spinner'
import renderGlobalAlert from '../../../../system/hooks/useGlobalAlert'
import { Tag, Tooltip, Select } from 'antd'
import { useTranslation } from 'react-i18next'
import { CheckBox } from '../../../../components/controls/checkbox'
import { LoadingStatus } from '../../../../@types/enumsGlobal'


interface IBunchInvoicesProps extends IGridProps {
    importedInvoiceItems: IImportedInvoiceItemDTO[]
    idInvoice: string
    isSucceed: boolean
    dataForMoveInvoices: IDataForMoveFiles
    callbackAfterImport: ()=> void
    skipDeleteOnClose?: boolean
    onClose?: () => void
    onSaved?: () => void
}

interface IMessageModalProps {
    show: boolean,
    message: string,
    handler?: () => void
}

export const BunchInvoices: FC<IBunchInvoicesProps> = (props) => {

    const appContext = useAppContext()
    const tabsContext = useTabsContext()

    const importedInvoiceDataProvider = new ImportedInvoiceDataProvider(appContext.coreApiService);
    const goodsDataProvider = new GoodsDataProvider(appContext.coreApiService)
    const registerPriceDataProvider = new RegisterPriceDataProvider(appContext.coreApiService)

    const [invoiceImportedItemsGridFilter, dispatchInvoiceImportedItemsGridFilter] = useGridFilter();
    const [goodsGridFilter, dispatchGoodsGridFilter] = useGridFilter(CreateSelectorGridFilter);

    const [invoiceSelectedItem, setInvoiceSelectedItem] = useState<IGridRowWithMultipleValue | undefined>(undefined);
    const [goodsSelectedItem, setGoodsSelectedItem] = useState<IGridRow>();

    const [importedInvoiceItemsTotalCount] = useState<number>(0);
    const [goodsTotalCount, setGoodsTotalCount] = useState<number>(0);

    const [goodsData, setGoodsData] = useState<IGoodsViewDTO[]>([])
    const [goodsName, setGoodsName] = useState<string>('');
    const [importedInvoiceItemsData, setImportedInvoiceItemsData] = useState<IImportedInvoiceItemViewDTO[]>([])
    const [showMessageModal, setShowMessageModal] = useState<IMessageModalProps>({ show: false, message: '' });
    const [showMessageModalBunch, setShowMessageModalBunch] = useState<IMessageModalProps>({ show: false, message: '' });
    const [showMessageGosContract, setShowMessageGosContract] = useState<IMessageModalProps>({ show: false, message: '' });
    const [showMessageRegisterPrice, setShowMessageRegisterPrice] = useState<IMessageModalProps>({ show: false, message: '' });

    const [goodsItem, setGoodsItem] = useState<IImportedInvoiceItemViewDTO | null>(null)

    const importInvoicesProvider = new ImportInvoicesProvider(appContext.coreApiService);

    const [importedInvoiceItems, setImportedInvoiceItems] = useState<IImportedInvoiceItemDTO[]>(props.importedInvoiceItems ?? []);
    const [loadingStatus, setLoadingStatus] = useState<LoadingStatus>(LoadingStatus.InProcess);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [isSubmittingSearch, setIsSubmittingSearch] = useState<boolean>(false);
    const [loader, setLoader] = useState<boolean>(false);
    const { t } = useTranslation();

    type SearchFormat = 'goods-country' | 'goods-producer-country' | 'goods-producer' | 'goods';

    const [searchFormat, setSearchFormat] = useState<SearchFormat>(() => {
        const saved = localStorage.getItem('bunchInvoices_searchFormat') as SearchFormat | null;
        return saved ?? 'goods-country';
    });

    const searchFormatOptions = [
        { label: 'Товар-Страна', value: 'goods-country' },
        { label: 'Товар-Производитель-Страна', value: 'goods-producer-country' },
        { label: 'Товар-Производитель', value: 'goods-producer' },
        { label: 'Товар', value: 'goods' },
    ];

    // Кэш реестровых цен товаров (idGlobal -> цены), чтобы не запрашивать их повторно
    const goodsRegisterPricesPromisesRef = useRef<Map<string, Promise<number[]>>>(new Map());
    const [goodsRegisterPrices, setGoodsRegisterPrices] = useState<Map<string, number[]>>(new Map());

    // Загрузка всех неудалённых реестровых цен товара с кэшированием по idGlobal
    const getGoodsRegisterPrices = (idGoodsGlobal: string): Promise<number[]> => {
        let promise = goodsRegisterPricesPromisesRef.current.get(idGoodsGlobal);
        if (!promise) {
            promise = new Promise<number[]>((resolve) => {
                registerPriceDataProvider.getByGoods(idGoodsGlobal, '1900-01-01', (entities) => {
                    resolve((entities ?? []).map(x => x.price));
                });
            });
            goodsRegisterPricesPromisesRef.current.set(idGoodsGlobal, promise);
        }
        return promise;
    };

    // Загрузка реестровых цен товара в стейт (для отображения в нижнем гриде)
    const loadGoodsRegisterPrices = (idGoodsGlobal: string) => {
        getGoodsRegisterPrices(idGoodsGlobal).then(prices => {
            setGoodsRegisterPrices(prev => prev.has(idGoodsGlobal) ? prev : new Map(prev).set(idGoodsGlobal, prices));
        });
    };

    // Реестровая цена из выбранной строки накладной (цена документа)
    const getSelectedInvoiceRegisterPrice = (): number | undefined => {
        if (!invoiceSelectedItem) return undefined;
        const idInvoiceItem = isArray(invoiceSelectedItem.idGlobal) ? invoiceSelectedItem.idGlobal[0] : invoiceSelectedItem.idGlobal;
        const item = importedInvoiceItemsData.find(x => (isArray(x.idGlobal) ? x.idGlobal[0] : x.idGlobal) === idInvoiceItem);
        if (!item) return undefined;
        return isArray(item.registerPrice) ? item.registerPrice[0] : item.registerPrice;
    };

    // Поиск реестровой цены товара, совпадающей с ценой из накладной (сравнение с точностью до копеек)
    const findMatchedRegisterPrice = (prices: number[] | undefined, invoiceRegisterPrice: number | undefined): number | undefined => {
        if (!invoiceRegisterPrice) return undefined;
        return prices?.find(p => Math.abs(p - invoiceRegisterPrice) < 0.005);
    };

    // Подстановка в строку подобранного товара реестровой цены, совпадающей с ценой накладной (если такая есть)
    const applyMatchedRegisterPrice = (goods: IGoodsViewDTO, invoiceRegisterPrice: number | undefined): IGoodsViewDTO => {
        if (!goods.important) return goods;
        const matched = findMatchedRegisterPrice(goodsRegisterPrices.get(goods.idGlobal), invoiceRegisterPrice);
        return matched !== undefined ? { ...goods, registerPrice: matched } : goods;
    };

    const invoiceRegisterPrice = useMemo(() => getSelectedInvoiceRegisterPrice(), [invoiceSelectedItem, importedInvoiceItemsData]);

    // Строки нижнего грида с подставленной реестровой ценой, совпадающей с ценой накладной
    const goodsViewData = useMemo(() => {
        if (!invoiceRegisterPrice) return goodsData;
        return goodsData.map(g => applyMatchedRegisterPrice(g, invoiceRegisterPrice));
    }, [goodsData, goodsRegisterPrices, invoiceRegisterPrice]);

    const buildSearchString = (row: IGridRowWithMultipleValue): string => {
        const getValue = (propName: string): string => {
            const val = row?.cells.find(c => c.propertyName === propName)?.value;
            return Array.isArray(val) ? val[0] ?? '' : val ?? '';
        };
        const parts: string[] = [];
        parts.push(getValue('goodsName'));
        if (searchFormat.includes('producer')) {
            parts.push(getValue('goodsProducerName'));
        }
        if (searchFormat.includes('country')) {
            parts.push(getValue('country'));
        }
        return parts.filter(p => p.trim() !== '').join(' ');
    };
    
    function sendMoveInvoicesToOldFolder()
    {
        if (props.dataForMoveInvoices)
        {
            let index = props.dataForMoveInvoices.idsInvoices.indexOf(props.idInvoice)
            if (index!=-1)
            {
                let dto: IPostFilesNameDTO = {
                    Names: [props.dataForMoveInvoices.fileName[index]]
                }
                importInvoicesProvider.moveFilesLocal(props.dataForMoveInvoices.idContractor, props.dataForMoveInvoices.idSupplier, dto, (data)=>{
                    props.callbackAfterImport?.();
                }, ()=>{} )
            }
        }

    }

    const closeCalledRef = useRef(false);

    const close = () => 
    {
        if (closeCalledRef.current) {
            tabsContext.closeCurrent();
            return;
        }
        closeCalledRef.current = true;

        if (!props.skipDeleteOnClose) {
            deleteImportedInvoice();
        }
        props.onClose?.();
        tabsContext.closeCurrent();
    }

    function saveAllBunch()
    {
        importedInvoiceDataProvider.saveGoodsCode(props.idInvoice, (e) => {
        })
    }

    function refreshAllBunch()
    {
        setInvoiceSelectedItem(undefined)
        importedInvoiceDataProvider.refreshGoodsCode(props.idInvoice, (g) => {
            setImportedInvoiceItemsData([]);

            importedInvoiceDataProvider.getById(props.idInvoice, (e) => {
                let errorList = g.data.errorsList.map(x => x.split(' ')[0]);
                let items = [...e.items.map(i => {
                    let c = g.data.type.find(z=>z.idInvoiceItem === i.idGlobal);
                    return ({ ...i, success: errorList.find(y => y === i.idGlobal) ? false : true, type: c?.type })
                }
                )] as any;
                setImportedInvoiceItems(items);
            })

            
        })
    }
    
    function unsetBunch() {
        if (invoiceSelectedItem) {

            let selectGoods = importedInvoiceItemsData.find(x => x.idGlobal[0] === invoiceSelectedItem?.idGlobal) as IImportedInvoiceItemViewDTO
            if (!selectGoods.connected) return;
            let idGlobalItem = isArray(invoiceSelectedItem?.idGlobal) ? invoiceSelectedItem?.idGlobal[0] : invoiceSelectedItem?.idGlobal;

            if (isArray(selectGoods?.idGlobal)) {
                importedInvoiceDataProvider.unBunchItem([{ idImportedInvoiceItemGlobal: idGlobalItem, idGoodGlobal: selectGoods.idGlobal[1] }] as IBunchItemBody[], e => {
                    setImportedInvoiceItemsData(importedInvoiceItemsData.map((x) => (x.idGlobal[0] === invoiceSelectedItem?.idGlobal ? {
                        idGlobal: x.idGlobal[0],
                        connected: false,
                        goodsCode: x.goodsCode[0],
                        goodsBarcode: x.goodsBarcode[0],
                        goodsName: x.goodsName[0],
                        goodsProducerName: x.goodsProducerName[0],
                        country: x.country[0],
                        registerPrice: x.registerPrice[0],
                        vat: x.vat[0],
                        success: false,
                        bunchType: BunchType.none,
                        isGnvls: false,
                        alert: ''
                    } : x)))
                    setInvoiceSelectedItem({ ...invoiceSelectedItem, success: false });
                });
            }
        }
    }

    const saveBunch = (goodsItem?: IGridRow) => {
        if (invoiceSelectedItem) {
            if (!goodsSelectedItem) return;
            importedInvoiceDataProvider.bunchItem([{ idImportedInvoiceItemGlobal: isArray(invoiceSelectedItem?.idGlobal) ? invoiceSelectedItem?.idGlobal[0] : invoiceSelectedItem?.idGlobal, idGoodGlobal: goodsSelectedItem?.idGlobal }] as IBunchItemBody[], e => {
                let selectGoods = importedInvoiceItemsData.find(x => x.idGlobal === invoiceSelectedItem?.idGlobal) as IImportedInvoiceItemViewDTO
                goodsDataProvider.getById(goodsItem?.idGlobal ?? goodsSelectedItem?.idGlobal as string, e => {
                    let goods = e;
                    const updateBunchItems = (goodsRegisterPrice: number) => {
                        setImportedInvoiceItemsData(importedInvoiceItemsData.map((element) => (element.idGlobal === invoiceSelectedItem?.idGlobal ? { ...element,
                            idGlobal: [isArray(selectGoods?.idGlobal) ? selectGoods.idGlobal[0] : selectGoods.idGlobal, goodsItem?.idGlobal ?? goodsSelectedItem?.idGlobal as string] as [string, string],
                            connected: true,
                            goodsCode: [isArray(selectGoods?.goodsCode) ? selectGoods.goodsCode[0] : selectGoods.goodsCode, goods.code] as [string, string],
                            goodsBarcode: [isArray(selectGoods?.goodsBarcode) ? selectGoods.goodsBarcode[0] : selectGoods.goodsBarcode, goods.barcode] as [string, string],
                            goodsName: [isArray(selectGoods?.goodsName) ? selectGoods.goodsName[0] : selectGoods.goodsName, goods.goodsName.name] as [string, string],
                            goodsProducerName: [isArray(selectGoods?.goodsProducerName) ? selectGoods.goodsProducerName[0] : selectGoods.goodsProducerName, goods.producer.displayName] as [string, string],
                            country: [isArray(selectGoods.country) ? selectGoods.country[0] : selectGoods.country, goods.country?.displayName ?? ''] as [string, string],
                            registerPrice: [isArray(selectGoods.registerPrice) ? selectGoods.registerPrice[0] : selectGoods.registerPrice, goodsRegisterPrice] as [number, number],
                            vat: [isArray(selectGoods.vat) ? selectGoods.vat[0] : selectGoods.vat, e.vatRetail ?? 0] as [number, number],
                            success: true,
                            bunchType: BunchType.manual,
                            isGnvls: [selectGoods.isGnvls?.[0] || false, goods.gnvls.important || false],
                            alert: ''
                        } : element)));
                        setInvoiceSelectedItem({ ...invoiceSelectedItem, success: true });
                        setGoodsSelectedItem(undefined); //нужно для того чтобы удалить выбраный элемент в нижнем гриде после привязки
                    };
                    // если у товара есть реестровая цена, совпадающая с ценой из накладной, используем именно её
                    const invoicePrice = isArray(selectGoods?.registerPrice) ? selectGoods.registerPrice[0] : selectGoods?.registerPrice;
                    if (goods.gnvls?.important && invoicePrice) {
                        getGoodsRegisterPrices(goodsItem?.idGlobal ?? goodsSelectedItem?.idGlobal as string).then(prices => {
                            updateBunchItems(findMatchedRegisterPrice(prices, invoicePrice) ?? goods.gnvls?.registerPrice ?? 0);
                        });
                    } else {
                        updateBunchItems(goods.gnvls?.registerPrice ?? 0);
                    }
                })
            })
        }
    }

    const unsetAndSaveBunch = async (goodsItem: IGridRow) => {

        if (invoiceSelectedItem) {
            
            let selectGoods = importedInvoiceItemsData.find(x => x.idGlobal[0] === invoiceSelectedItem?.idGlobal) as IImportedInvoiceItemViewDTO
            if (!selectGoods.connected) return;
            let idGlobalItem = isArray(invoiceSelectedItem?.idGlobal) ? invoiceSelectedItem?.idGlobal[0] : invoiceSelectedItem?.idGlobal;
            
            if (isArray(selectGoods?.idGlobal)) {
                
                //отвязка
                await new Promise((resolve) => importedInvoiceDataProvider.unBunchItem([{ idImportedInvoiceItemGlobal: idGlobalItem, idGoodGlobal: selectGoods.idGlobal[1] }] as IBunchItemBody[], e => resolve(e)));
                //привязка
                await new Promise((resolve) => importedInvoiceDataProvider.bunchItem([{ idImportedInvoiceItemGlobal: isArray(invoiceSelectedItem?.idGlobal) ? invoiceSelectedItem?.idGlobal[0] : invoiceSelectedItem?.idGlobal, idGoodGlobal: goodsItem?.idGlobal }] as IBunchItemBody[], e => resolve(e)));

                const e = await new Promise<IGoodsDTO>(resolve =>  goodsDataProvider.getById(goodsItem?.idGlobal as string, e => resolve(e)));
                const goods = e;

                // если у товара есть реестровая цена, совпадающая с ценой из накладной, используем именно её
                const invoicePrice = isArray(selectGoods?.registerPrice) ? selectGoods.registerPrice[0] : selectGoods?.registerPrice;
                let goodsRegisterPrice = goods.gnvls?.registerPrice ?? 0;
                if (goods.gnvls?.important && invoicePrice) {
                    const prices = await getGoodsRegisterPrices(goodsItem?.idGlobal as string);
                    goodsRegisterPrice = findMatchedRegisterPrice(prices, invoicePrice) ?? goodsRegisterPrice;
                }

                setImportedInvoiceItemsData(importedInvoiceItemsData.map((element) => (element.idGlobal[0] === invoiceSelectedItem?.idGlobal ? {
                    idGlobal: [isArray(element?.idGlobal) ? element.idGlobal[0] : selectGoods.idGlobal, goodsItem?.idGlobal ?? '' as string] as [string, string],
                    connected: true,
                    goodsCode: [isArray(element?.goodsCode) ? element.goodsCode[0] : selectGoods.goodsCode, goods.code ?? ''] as [string, string],
                    goodsBarcode: [isArray(element?.goodsBarcode) ? element.goodsBarcode[0] : selectGoods.goodsBarcode, goods.barcode ?? ''] as [string, string],
                    goodsName: [isArray(element?.goodsName) ? element.goodsName[0] : selectGoods.goodsName, goods.goodsName.name ?? ''] as [string, string],
                    goodsProducerName: [isArray(element?.goodsProducerName) ? element.goodsProducerName[0] : selectGoods.goodsProducerName, goods.producer.displayName ?? ''] as [string, string],
                    country: [isArray(element.country) ? element.country[0] : selectGoods.country, goods.country?.displayName ?? ''] as [string, string],
                    registerPrice: [isArray(element.registerPrice) ? element.registerPrice[0] : selectGoods.registerPrice, goodsRegisterPrice] as [number, number],
                    vat: [isArray(element.vat) ? element.vat[0] : selectGoods.vat, e.vatRetail ?? 0] as [number, number],
                    success: true,
                    bunchType: BunchType.manual,
                    isGnvls: [isArray(element.isGnvls) ? element.isGnvls[0] : selectGoods.isGnvls, goods.gnvls.important || false] as [boolean, boolean],
                    alert: ''
                } : element)));

            }
        }
    }

    const deleteImportedInvoice = () => {
        importedInvoiceDataProvider.deleteImportedInvoice(props.idInvoice)
    }

    const createInvoice = () => {
        importedInvoiceDataProvider.createInvoice(props.idInvoice, (e) => {
            if (e.respType === "isFailed") { 
                close()
                return
            }
            props.callbackAfterImport?.();
            sendMoveInvoicesToOldFolder()
            setIsSubmitting(false);

            if (props.onSaved) {
                props.onSaved();
                close();
            } else {
                setShowMessageModal({
                    show: true, 
                    message: 'Товар импортирован успешно, желаете перейти в "Приходные накладные" для дальнейшей работы?',
                    handler: () => {
                        close()
                        tabsContext.open('invoice_plugin')
                    }
                })
            }
        })
    }

    const onSave = () => {
        if (props.isSucceed) {
            setIsSubmitting(true);
            importedInvoiceDataProvider.importedValidateGosContracts(props.idInvoice, (e) => {
                if (e.result && e.message === null) {
                    checkPriceMismatchesAndProceed();
                }
                else if (e.result && e.message !== '') {
                    setShowMessageGosContract({show: true, message: e.message, handler() {
                        checkPriceMismatchesAndProceed();
                    },})
                }
                else if (!e.result) {
                    setShowMessageGosContract({show: true, message: e.message, handler() {
                        setIsSubmitting(false)
                    }})
                }
            })
        } else {
            if (importedInvoiceItemsData.filter(x => x.connected === false).length > 0) {
                setShowMessageModalBunch({ show: true, message: 'Свяжите все позиции' })
            } else {
                setIsSubmitting(true);
                createInvoice()
            }
        }
    }

    const checkPriceMismatchesAndProceed = () => {
        const priceMismatches: string[] = [];
        
        importedInvoiceItemsData.forEach(item => {
            let isGnvls = false;

            if (Array.isArray(item.isGnvls) && item.isGnvls.length > 0) {
                isGnvls = item.isGnvls[0];
            } else {
                isGnvls = item.isGnvls as boolean || false;
            }
            
            if (!isGnvls) return;

            if (item.connected && Array.isArray(item.registerPrice) && item.registerPrice.length > 1) {
                const [registerPrice, supplierPrice] = item.registerPrice;
                
                let goodsName = '';
                if (Array.isArray(item.goodsName)) {
                    goodsName = item.goodsName[0] || item.goodsName[1];
                } else {
                    goodsName = item.goodsName;
                }
                
                if (registerPrice === 0) {
                    priceMismatches.push(
                        `${goodsName}:\n ${t('documents.bunchInvoices.isNotSpecifiedRegistryPrice')}\n`
                    );
                }
                if (registerPrice !== supplierPrice) {
                    priceMismatches.push(
                        `${goodsName}:\n ${t('documents.bunchInvoices.invalidRegistryPrice')}\n`
                    );
                }
            }
        });
        
        if (priceMismatches.length > 0) {
            const formattedMessage = priceMismatches.join('\n ');
            setShowMessageRegisterPrice({
                show: true,
                message: `${t('documents.bunchInvoices.detectInvalidRegistryPrice')}\n\n ${formattedMessage}\n\n`,
                handler() {
                    createInvoice();
                }
            });
        } else {
            createInvoice();
        }
    };

    const searchHandler = (value: string) => {
        setGoodsName(value)
        dispatchGoodsGridFilter({
            ...goodsGridFilter,
            type: "paramFilter",
            payload: {
                gridParamFilter: {
                    goodsName: value,
                },
                sortDesc: "Perc"
            },
        })
    }

    const getBunchType = (type: string | undefined): BunchType => {
        if (!type) return BunchType.none;

        const enumValues = Object.values(BunchType);
        if (enumValues.includes(type as BunchType)) {
            return type as BunchType;
        }

        return BunchType.none;
    };

    useEffect(() => {
        if (props.skipDeleteOnClose) return;

        const handleTabClose = (event) => {
            deleteImportedInvoice();
            return (event.returnValue = "");
        };

        window.addEventListener("beforeunload", handleTabClose);

        return () => {
            window.removeEventListener("beforeunload", handleTabClose);
        };
    }, []);

    useEffect(() => {
        let isMounted = true;
        let isProcessing = false;

        const processImportedItems = async () => {
            if (isProcessing || !importedInvoiceItems || importedInvoiceItems.length === 0) {
                return;
            }

            isProcessing = true;
            const newItems: IImportedInvoiceItemViewDTO[] = [];

            try {
                // Разделяем элементы на две группы: с idGoodsExternalGlobal и без
                const itemsWithGoodsId = importedInvoiceItems.filter(
                    x => x.idGoodsExternalGlobal && x.idGoodsExternalGlobal !== ''
                );
                const itemsWithoutGoodsId = importedInvoiceItems.filter(
                    x => !x.idGoodsExternalGlobal || x.idGoodsExternalGlobal === ''
                );

                // Обрабатываем элементы без ID (создаем пустые записи)
                itemsWithoutGoodsId.forEach(x => {
                    if (!isMounted) return;
                    const tmpToUpperCase = (tmp: string) => tmp?.toUpperCase() || '';
                    newItems.push({
                        idGlobal: x.idGlobal,
                        connected: false,
                        goodsCode: x.goodsСode && tmpToUpperCase(x.goodsСode),
                        goodsBarcode: x.barcode && tmpToUpperCase(x.barcode),
                        goodsName: x.goodsName && tmpToUpperCase(x.goodsName),
                        goodsProducerName: x.producer && tmpToUpperCase(x.producer),
                        country: x.country && tmpToUpperCase(x.country),
                        registerPrice: x.registerPrice,
                        vat: x.supplierVat,
                        success: x.success,
                        bunchType: BunchType.none,
                        isGnvls: x.isImportant || false,
                        alert: ''
                    } as IImportedInvoiceItemViewDTO);
                });

                // Обрабатываем элементы с ID - пачками по 50
                if (itemsWithGoodsId.length > 0) {
                    const BATCH_SIZE = 50;
                    const batches: IImportedInvoiceItemDTO[][] = [];

                    // Разбиваем на пачки по 50
                    for (let i = 0; i < itemsWithGoodsId.length; i += BATCH_SIZE) {
                        batches.push(itemsWithGoodsId.slice(i, i + BATCH_SIZE));
                    }

                    // Обрабатываем каждую пачку
                    for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
                        if (!isMounted) break;

                        const batch = batches[batchIndex];
                        const ids = batch
                            .map(x => x.idGoodsExternalGlobal)
                            .filter((id): id is string => !!id && id !== '');

                        if (ids.length === 0) {
                        // Если все ID пустые, создаем пустые записи
                        batch.forEach(x => {
                            const tmpToUpperCase = (tmp: string) => tmp?.toUpperCase() || '';
                            newItems.push({
                                idGlobal: x.idGlobal,
                                connected: false,
                                goodsCode: x.goodsСode && tmpToUpperCase(x.goodsСode),
                                goodsBarcode: x.barcode && tmpToUpperCase(x.barcode),
                                goodsName: x.goodsName && tmpToUpperCase(x.goodsName),
                                goodsProducerName: x.producer && tmpToUpperCase(x.producer),
                                country: x.country && tmpToUpperCase(x.country),
                                registerPrice: x.registerPrice,
                                vat: x.supplierVat,
                                success: x.success,
                                bunchType: BunchType.none,
                                isGnvls: x.isImportant || false,
                                alert: 'ID товара не указан'
                            } as IImportedInvoiceItemViewDTO);
                        });
                        continue;
                    }

                        // Ждем ответ для всей пачки
                        await new Promise<void>((resolve) => {
                            goodsDataProvider.getByManyId(ids, (response: IGoodsDTO[]) => {
                                
                                if (!isMounted) {
                                    resolve();
                                    return;
                                }

                                const resultsMap = new Map<string, IGoodsDTO>();
                                if (Array.isArray(response)) {
                                    response.forEach(item => {
                                        if (item?.idGlobal) {
                                            resultsMap.set(item.idGlobal, item);
                                        }
                                    });
                                }

                                // Обрабатываем каждый элемент в пачке
                                batch.forEach(x => {
                                    if (!isMounted) return;

                                    const goodsData = resultsMap.get(x.idGoodsExternalGlobal);

                                    if (goodsData) {
                                        // Определяем registerPrice в зависимости от isSucceed
                                        let registerPriceValue;
                                        if (props.isSucceed) {
                                            registerPriceValue = goodsData.gnvls?.registerPrice ?? 0;
                                        } else {
                                            registerPriceValue = goodsData.gnvls?.registerPrice ?? 0;
                                        }

                                        const item: IImportedInvoiceItemViewDTO = {
                                            idGlobal: [x.idGlobal, x.idGoodsExternalGlobal],
                                            connected: true,
                                            goodsCode: generateToUpperCase([x.goodsСode, goodsData.code]),
                                            goodsBarcode: generateToUpperCase([x.barcode, goodsData.barcode]),
                                            goodsName: generateToUpperCase([x.goodsName, goodsData.goodsName?.name]),
                                            goodsProducerName: generateToUpperCase([x.producer, goodsData.producer?.displayName]),
                                            country: x.country && generateToUpperCase([x.country, goodsData.country?.displayName]),
                                            registerPrice: [x.registerPrice, registerPriceValue],
                                            vat: [x.supplierVat, goodsData.vatRetail],
                                            success: x.success,
                                            bunchType: getBunchType(x.type),
                                            isGnvls: [x.isImportant || false, goodsData.gnvls?.important || false],
                                            alert: ''
                                        };
                                        newItems.push(item);
                                    } else {
                                        // Если товар не найден - создаем несвязанную запись
                                        const tmpToUpperCase = (tmp: string) => tmp?.toUpperCase() || '';
                                        newItems.push({
                                            idGlobal: x.idGlobal,
                                            connected: false,
                                            goodsCode: x.goodsСode && tmpToUpperCase(x.goodsСode),
                                            goodsBarcode: x.barcode && tmpToUpperCase(x.barcode),
                                            goodsName: x.goodsName && tmpToUpperCase(x.goodsName),
                                            goodsProducerName: x.producer && tmpToUpperCase(x.producer),
                                            country: x.country && tmpToUpperCase(x.country),
                                            registerPrice: x.registerPrice,
                                            vat: x.supplierVat,
                                            success: x.success,
                                            bunchType: BunchType.none,
                                            isGnvls: x.isImportant || false,
                                            alert: ''
                                        } as IImportedInvoiceItemViewDTO);
                                    }
                                });

                                resolve();
                            });
                        });

                        // Небольшая задержка между пачками, чтобы не перегружать сервер
                        if (batchIndex < batches.length - 1) {
                            await new Promise(resolve => setTimeout(resolve, 100));
                        }
                    }
                }

                // Обновляем состояние один раз после обработки всех пачек
                if (isMounted && newItems.length > 0) {
                    setImportedInvoiceItemsData(newItems);
                    setLoadingStatus(newItems.length > 0 ? LoadingStatus.Completed : LoadingStatus.NoData);
                }
            } catch (error) {
                console.error('Error processing imported items:', error);
            } finally {
                isProcessing = false;
            }
        };

        // Очищаем старые данные перед загрузкой новых
        setImportedInvoiceItemsData([]);
        processImportedItems();

        return () => {
            isMounted = false;
        };
    }, [importedInvoiceItems, props.isSucceed]);

    useEffect(() => {
        let goods = '';

        if (invoiceSelectedItem) {
            goods = buildSearchString(invoiceSelectedItem);
        } else if (importedInvoiceItemsData && importedInvoiceItemsData.length > 0) {
            const firstItem = importedInvoiceItemsData[0];
            if (firstItem && firstItem.goodsName) {
                goods = Array.isArray(firstItem.goodsName)
                    ? firstItem.goodsName[0] || ''
                    : firstItem.goodsName;
            }
        }

        if (goods && goods.trim() !== '') {
            setGoodsName(goods);
            if (invoiceSelectedItem) {
                searchHandler(goods);
            }
        } else {
            setGoodsName('');
        }
    }, [invoiceSelectedItem, importedInvoiceItemsData, searchFormat]);

    const generateToUpperCase = (arr) => arr.map(item => item && item.toUpperCase());

    useEffect(() => {
        if (!goodsGridFilter.paramFilter) return;
        if (goodsGridFilter.paramFilter.length === 0) return;

        const abortController = new AbortController()
        const signal = abortController.signal
        setIsSubmittingSearch(true)

        goodsDataProvider.getViewByNameSmart(goodsGridFilter, (entities, totalCount) => {
            setGoodsData(entities)
            setGoodsTotalCount(totalCount)
            setIsSubmittingSearch(false)
        },signal);

        return () => {
            abortController.abort('abort')
        };

    }, [goodsGridFilter])

    // Загрузка реестровых цен ЖНВЛП-кандидатов, если в выбранной строке накладной задана реестровая цена
    useEffect(() => {
        if (!invoiceRegisterPrice) return;
        goodsData.forEach(g => {
            if (g.important && g.idGlobal) loadGoodsRegisterPrices(g.idGlobal);
        });
    }, [goodsData, invoiceRegisterPrice])

    function addBunch() {

        if (invoiceSelectedItem && isArray(invoiceSelectedItem?.idGlobal) === false) {
            setLoader(true);
            
            importedInvoiceDataProvider.createGoods(invoiceSelectedItem?.idGlobal as string, (e, err) => {
                if (err) {
                    setLoader(false);
                    renderGlobalAlert({
                        variant: "error",
                        title: 'Ошибка',
                        statusCode: 500,
                    });

                    return;
                }

                setInvoiceSelectedItem({...invoiceSelectedItem, success: true});
                importedInvoiceDataProvider.viewImportedInvoicesItemById(props.idInvoice, invoiceSelectedItem?.idGlobal as string, (data) => {
                    
                    setImportedInvoiceItemsData((prev) => {
                        return prev.map((elem) => {
                            if (elem.idGlobal === invoiceSelectedItem?.idGlobal) {
                                return {
                                    ...elem,
                                    idGlobal: [elem.idGlobal, data.idGlobal],
                                    connected: true,
                                    goodsCode: [elem.goodsCode, data.goodsСode],
                                    goodsBarcode: elem.goodsBarcode,
                                    goodsName: [elem.goodsName, data?.goodsName],
                                    goodsProducerName: [elem.goodsProducerName, data?.producer],
                                    country: [elem.country, data?.country],
                                    registerPrice: [elem.registerPrice, data.registerPrice],
                                    vat: [elem.vat, data.supplierCostInfo.vat],
                                    success: true,
                                    bunchType: BunchType.manual,
                                    isGnvls: [elem.isGnvls?.[0] || false, data.isGnvls || false],
                                    alert: ''
                                } as IImportedInvoiceItemViewDTO;
                            } else {
                                return elem;
                            }
                        });
                    });

                    setLoader(false)
                })

            })

        }
    }

    return (
        <BaseBunchInvoicesCreator
            ok={{
                onClick: () => {
                    onSave();
                },
                disabled: isSubmitting || importedInvoiceItemsData.filter((element) => !element.connected).length !== 0,
                sendRequestGlobal: isSubmitting,
            }}
            cancel={() => {
                close()
            }}
            saveBuch={() => { saveAllBunch() }}
            autoCreateGoods={() => { }}
            autoSelectionGoods={() => { }}
        >
            <PluginWrapper>
                <ToolbarWrapper>
                    <CommandsPanel
                        add={{
                            onClick: addBunch,
                            disabled: invoiceSelectedItem?.success === false ? false : true,
                        }}
                        save={{
                            onClick: () => { saveBunch() },
                            disabled: goodsSelectedItem && invoiceSelectedItem ? false : true
                        }}
                        delete={{
                            onClick: () => { unsetBunch() },
                            disabled: invoiceSelectedItem?.success ? false : true
                        }}
                        refresh={{
                            onClick: () => { refreshAllBunch() },
                            disabled: false
                        }}
                    />
                </ToolbarWrapper>
                <div className={styles2.title}>
                </div>
                {loader && <Spinner />}
                <div className={styles.gridWrapper}>
                    <div className={styles.masterGrid}>
                        <BunchGrid
                            gridId={props.gridId}
                            data={importedInvoiceItemsData}
                            filter={invoiceImportedItemsGridFilter}
                            hiddenPagination = {undefined}
                            totalCount={importedInvoiceItemsTotalCount}
                            plugin={props.plugin}
                            separator
                            searching
                            loadingStatus={loadingStatus}
                            selectedItem={invoiceSelectedItem}
                            onSelect={(row) => {
                                setInvoiceSelectedItem(row ? row : undefined);
                                setGoodsData([])
                            }}
                            onSort={(i, data) => {
                                data && setImportedInvoiceItemsData(data as IImportedInvoiceItemViewDTO[]);
                                invoiceSelectedItem && setInvoiceSelectedItem(undefined);
                            }}
                            onFilterDelete={(i) => { dispatchInvoiceImportedItemsGridFilter({ type: "deleteColumnFilter", payload: i.propertyName }) }}
                            onPageNumberChange={(n) => { dispatchInvoiceImportedItemsGridFilter({ type: "changePageNumber", payload: { pageNumber: n } }) }}
                            onNumberPerPageChange={(n) => { dispatchInvoiceImportedItemsGridFilter({ type: "changeNumberPerPage", payload: { numberPerPage: n } }) }}
                            renderCell={(cell, row, rowHeight) => {
                                if (cell.propertyName === 'alert') {
                                    const currentItem = importedInvoiceItemsData?.find(item => {
                                        const itemId = Array.isArray(item.idGlobal) ? item.idGlobal[0] : item.idGlobal;
                                        const rowId = Array.isArray(row.idGlobal) ? row.idGlobal[0] : row.idGlobal;
                                        return itemId === rowId;
                                    });
                                    
                                    if (currentItem?.connected && Array.isArray(currentItem.registerPrice) && currentItem.registerPrice.length > 1) {
                                        const [registerPrice, supplierPrice] = currentItem.registerPrice;
                                        const isGnvls = Array.isArray(currentItem.isGnvls)
                                            ? currentItem.isGnvls[0]
                                            : (currentItem.isGnvls || false);

                                        const getTitle = (registerPrice: number, supplierPrice: number) => {
                                            if(registerPrice === 0) return t('documents.bunchInvoices.isNotSpecifiedRegistryPrice')
                                            if(registerPrice !== supplierPrice) return t('documents.bunchInvoices.invalidRegistryPrice')
                                        }

                                        if (isGnvls &&  (registerPrice !== supplierPrice || registerPrice === 0)) {
                                            const circleSize = (rowHeight || 44) - 18;  
                                            return (
                                                <Tooltip
                                                    title={getTitle(registerPrice, supplierPrice)}
                                                    placement={'left'}
                                                >
                                                    <Tag
                                                        color="#cc3333"
                                                        style={{
                                                            margin: 0,
                                                            borderRadius: '50%',
                                                            width: `${circleSize}px`,
                                                            height: `${circleSize}px`,                                                            
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            padding: 0,
                                                            fontSize: '16px'
                                                        }}
                                                    >
                                                        !
                                                    </Tag>
                                                </Tooltip>
                                            );
                                        }
                                    }
                                    return null;
                                }
                                if (cell.propertyName === 'isGnvls') {
                                    const currentItem = importedInvoiceItemsData?.find(item => {
                                        const itemId = Array.isArray(item.idGlobal) ? item.idGlobal[0] : item.idGlobal;
                                        const rowId = Array.isArray(row.idGlobal) ? row.idGlobal[0] : row.idGlobal;
                                        return itemId === rowId;
                                    });

                                    if (currentItem) {
                                        let docGnvls = false;
                                        let goodsGnvls = false;

                                        if (Array.isArray(currentItem.isGnvls) && currentItem.isGnvls.length > 1) {
                                            docGnvls = currentItem.isGnvls[0] || false;
                                            goodsGnvls = currentItem.isGnvls[1] || false;
                                            return (
                                                <div className={styles2.wrapperCheckBoxIsGnvls_multiple}>
                                                    <CheckBox defaultChecked={docGnvls} />
                                                    <CheckBox defaultChecked={goodsGnvls} />
                                                </div>
                                            );
                                        } else {
                                            docGnvls = currentItem.isGnvls as boolean || false;
                                            return (
                                                <div className={styles2.wrapperCheckBoxIsGnvls}>
                                                    <CheckBox defaultChecked={docGnvls} />
                                                </div>
                                            );
                                        }
                                    }
                                    return null;
                                }
                                return null;
                            }}
                        />
                    </div>
                </div>
                <div className={styles2.searchPanel}>
                    <span className={styles2.searchTitle}>Подобранные товары</span>
                    <Select
                        style={{ width: 220, marginLeft: 8, marginRight: 8 }}
                        value={searchFormat}
                        options={searchFormatOptions}
                        onChange={(value) => {
                            setSearchFormat(value as SearchFormat);
                            localStorage.setItem('bunchInvoices_searchFormat', value);
                        }}
                    />
                    <TextInput
                        className={styles2.searchGoods}
                        value={goodsName}
                        onChange={(e) => {
                            setGoodsName(e)
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') { 
                                let value = e.currentTarget.value
                                searchHandler(value)
                            }
                        }}
                    />
                     <div className={styles2.searchIcon}>
                        <SearchButton sizeVariant="mini" onClick={() => {
                            searchHandler(goodsName)
                        }
                    }/>
                    </div>
                </div>
                <div className={styles.gridWrapper}>
                    <div className={styles.masterGrid}> {
                        invoiceSelectedItem && (
                            isSubmittingSearch ? (
                                <Spinner />
                                ) :     
                                <DefaultGrid
                                    separator
                                    quantityPosition
                                    autoSelect
                                    gridId={uuidv4()}
                                    data={goodsViewData}
                                    totalCount={goodsTotalCount}
                                    filter={goodsGridFilter}
                                    plugin={GoodsBunchGridPluginSettings}
                                    selectedItem={goodsSelectedItem}
                                    dataProvider={goodsDataProvider}
                                    getView={(gridFilter,callback) => {
                                        goodsDataProvider.getViewByNameSmart(gridFilter, (entities) => {
                                            if (invoiceRegisterPrice) {
                                                entities.forEach(g => { if (g.important && g.idGlobal) loadGoodsRegisterPrices(g.idGlobal); });
                                            }
                                            callback(entities.map(g => applyMatchedRegisterPrice(g, invoiceRegisterPrice)))
                                        })
                                    }}
                                    onSelect={(row) => {
                                        setGoodsSelectedItem(row? row : undefined)
                                    }}
                                    onDoubleClick={(row) => {
                                        setGoodsSelectedItem(row)
                                        if (invoiceSelectedItem) {
                                            invoiceSelectedItem.success ? unsetAndSaveBunch(row) : saveBunch(row)
                                        }
                                    }}
                                    onSort={(i) => { dispatchGoodsGridFilter({ type: "sort", payload: i.propertyName }) }}
                                    onFilterDelete={(i) => { dispatchGoodsGridFilter({ type: "deleteColumnFilter", payload: i.propertyName }) }}
                                    onPageNumberChange={(n) => { dispatchGoodsGridFilter({ type: "changePageNumber", payload: { pageNumber: n } }) }}
                                    onNumberPerPageChange={(n) => { dispatchGoodsGridFilter({ type: "changeNumberPerPage", payload: { numberPerPage: n } }) }}
                                    searching
                                />
                        )
                        }
                    </div>
                </div>
                {
                    showMessageModal.show &&
                    <MessageModalWindow
                        ok={{ onClick: () => { 
                            if (showMessageModal.handler) { 
                                showMessageModal.handler() 
                            }
                            setShowMessageModal({ show: false, message: '' }) 
                        }}}
                        cancel={{ onClick: () => {
                            setShowMessageModal({ show: false, message: '' }) 
                            close();
                            } }}
                        message={showMessageModal.message}
                    />
                }
                {
                    showMessageModalBunch.show &&
                    <MessageModalWindow
                        ok={{ onClick: () => { 
                            if (showMessageModalBunch.handler) { 
                                showMessageModalBunch.handler() 
                            }
                            setShowMessageModalBunch({ show: false, message: '' }) 
                        }}}
                        cancel={{ onClick: () => {
                            setShowMessageModalBunch({ show: false, message: '' }) 
                            } }}
                        message={showMessageModalBunch.message}
                    />
                }
                {
                    showMessageGosContract.show &&
                    <MessageModalWindow
                        ok={{ onClick: () => { 
                            if (showMessageGosContract.handler) { 
                                showMessageGosContract.handler() 
                            }
                            setShowMessageGosContract({ show: false, message: '' }) 
                        }}}
                        cancel={{ 
                            hidden: true,
                            onClick() {},
                        }}
                        message={showMessageGosContract.message}
                    />
                }
                {
                    showMessageRegisterPrice.show &&
                    <MessageModalWindow
                        ok={{ onClick: () => { 
                            if (showMessageRegisterPrice.handler) { 
                                showMessageRegisterPrice.handler() 
                            }
                            setShowMessageRegisterPrice({ show: false, message: '' }) 
                        }}}
                        cancel={{ 
                            hidden: false,
                            onClick: () => {
                                setShowMessageRegisterPrice({ show: false, message: '' })
                                setIsSubmitting(false)
                            },
                        }}
                        message={showMessageRegisterPrice.message}
                    />
                }
            </PluginWrapper>
        </BaseBunchInvoicesCreator>
    )
}